import { MoreVerticalIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { ErrorMessage } from "@/app/components/error-message";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PublicUser, WithPagination } from "@classview/shared-types";
import { useQueryClient } from "@tanstack/react-query";

import { useRemoveUserFromOrg } from "../api/remove-user-from-org";

type UserSaRowActionsProps = {
  userId: string;
};

/**
 * This component is used to DELETE a user.
 * This is used for SUPERADMIN
 * For removing a user from an organisation, check out user-row-actions.tsx
 */
export function UserSaRowActions(props: UserSaRowActionsProps) {
  const { id: organisationId } = useParams<{ id: string }>();
  const {
    mutateAsync: removeUserFromOrg,
    isPending,
    isSuccess,
    isError,
    error,
    reset,
  } = useRemoveUserFromOrg({
    onSuccess: () => {
      queryClient.setQueriesData(
        { queryKey: ["users"], exact: false },
        (oldData: WithPagination<PublicUser> | undefined) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            items: oldData.items.filter((user) => user.id !== props.userId),
          };
        },
      );
    },
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const queryClient = useQueryClient();

  useEffect(() => {
    if (!isDeleting) {
      reset();
    }
  }, [isDeleting, reset]);

  const handleDelete = async () => {
    try {
      await removeUserFromOrg({
        userId: props.userId,
        orgId: organisationId ?? "",
      });
      queryClient.invalidateQueries({
        queryKey: ["users", organisationId],
      });
      setIsDeleting(false);
    } catch (error) {
      console.error(error);
    }
  };

  /**
   * You can be on: Superadmin -> Users (deleting a user is  not possible from here)
   * You can be on: Superadmin -> Organisations -> Users (deleting a user is possible from here)
   */
  const isAllUsersScreen = !organisationId;
  return (
    <Dialog onOpenChange={setIsDeleting} open={isDeleting}>
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon">
            <MoreVerticalIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem asChild>
            <Link
              to={
                organisationId
                  ? `/admin/organisations/${organisationId}/users/${props.userId}`
                  : `/admin/users/${props.userId}`
              }
            >
              Edit
            </Link>
          </DropdownMenuItem>
          {!isAllUsersScreen && (
            <DialogTrigger asChild>
              <DropdownMenuItem className="text-destructive">
                Delete
              </DropdownMenuItem>
            </DialogTrigger>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Are you sure you want to remove this user from your organisation?
          </DialogTitle>
          <DialogDescription>This action cannot be undone.</DialogDescription>
        </DialogHeader>
        {isError && <ErrorMessage error={error} />}
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" disabled={isPending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="submit"
            variant="destructive"
            onClick={handleDelete}
            disabled={isPending || isSuccess}
            isLoading={isPending || isSuccess}
            isAnimated
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
